import React, { useState, useEffect, useRef } from 'react';
import { CONFIG } from '../config';

const ACCEPTED_TYPES = '.pdf,.txt,.docx,.doc,.csv,.md';

const KnowledgeManagementPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kbDisabled, setKbDisabled] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);

  const [loadingDownload, setLoadingDownload] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // s3_key pending confirmation

  const [hasKb, setHasKb] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null); // { type: 'success'|'error', text }

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      setKbDisabled(false);

      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/knowledge-base`, {
        credentials: 'include',
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || data.error || `Failed to fetch: ${resp.statusText}`);
      }

      const data = await resp.json();

      if (data.kb_disabled) {
        setKbDisabled(true);
        setDocuments([]);
        setHasKb(false);
      } else {
        setDocuments(data.documents || []);
        setHasKb(!!data.has_kb);
      }
    } catch (err) {
      console.error('Error fetching KB documents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setSelectedFile(file);
    setUploadError(null);
    setUploadSuccess(null);
  };

  const handleSyncKB = async () => {
    setSyncLoading(true);
    setSyncMessage(null);
    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/knowledge-base/sync`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || data.error || `Sync failed (${resp.status})`);
      }
      setSyncMessage({ type: 'success', text: 'AI knowledge base sync started. New documents will be searchable shortly.' });
      setTimeout(() => setSyncMessage(null), 8000);
    } catch (err) {
      console.error('Sync error:', err);
      setSyncMessage({ type: 'error', text: `Sync failed: ${err.message}` });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const filename = selectedFile.name;
      const contentType = selectedFile.type || 'application/octet-stream';

      // Step 1: Get presigned upload URL from backend
      const urlResp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/knowledge-base/upload-url?filename=${encodeURIComponent(filename)}&content_type=${encodeURIComponent(contentType)}`,
        { credentials: 'include' }
      );

      if (!urlResp.ok) {
        const data = await urlResp.json();
        throw new Error(data.detail || data.error || 'Failed to get upload URL');
      }

      const { upload_url } = await urlResp.json();

      // Step 2: PUT file directly to S3 using presigned URL
      const s3Resp = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: selectedFile,
      });

      if (!s3Resp.ok) {
        throw new Error(`S3 upload failed: ${s3Resp.status} ${s3Resp.statusText}`);
      }

      setUploadSuccess(`"${filename}" uploaded successfully.`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Refresh document list
      await fetchDocuments();

      // Auto-trigger KB sync after successful upload
      if (hasKb) {
        handleSyncKB();
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      setLoadingDownload(doc.s3_key);

      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/knowledge-base/download-url?s3_key=${encodeURIComponent(doc.s3_key)}`,
        { credentials: 'include' }
      );

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || data.error || 'Failed to get download URL');
      }

      const { download_url } = await resp.json();
      window.open(download_url, '_blank');
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download: ' + err.message);
    } finally {
      setLoadingDownload(null);
    }
  };

  const handleDeleteConfirm = (s3Key) => {
    setDeleteConfirm(s3Key);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const handleDelete = async (s3Key) => {
    try {
      setDeleting(s3Key);
      setDeleteConfirm(null);

      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/knowledge-base/document?s3_key=${encodeURIComponent(s3Key)}`,
        { method: 'DELETE', credentials: 'include' }
      );

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || data.error || 'Failed to delete document');
      }

      setDocuments(prev => prev.filter(d => d.s3_key !== s3Key));
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes == null) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="whatsapp-responses-page">
      <h2 className="page-title">Knowledge Management</h2>

      {loading && <div className="loading-message">Loading documents...</div>}
      {error && <div className="error-message">Error: {error}</div>}

      {!loading && kbDisabled && (
        <div className="no-messages">
          Knowledge base is not enabled for the active event. Enable Lex in Event Management to use this feature.
        </div>
      )}

      {!loading && !kbDisabled && (
        <>
          {/* Upload Section */}
          <div className="settings-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Upload Document</h3>
              <button
                onClick={handleSyncKB}
                disabled={syncLoading || !hasKb}
                className="btn-refresh"
                title={!hasKb ? 'AI knowledge base not yet provisioned' : 'Sync uploaded documents to AI knowledge base'}
                style={{ fontSize: '0.85rem' }}
              >
                {syncLoading ? 'Syncing...' : 'Sync to AI'}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                onChange={handleFileChange}
                className="form-input"
                style={{ flex: '1', minWidth: '200px' }}
              />
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="btn-refresh"
                style={{ whiteSpace: 'nowrap' }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
            <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#666' }}>
              Accepted: PDF, TXT, DOCX, DOC, CSV, MD
            </div>
            {uploadError && <div className="error-message" style={{ marginTop: '8px' }}>Upload failed: {uploadError}</div>}
            {uploadSuccess && <div className="success-message" style={{ marginTop: '8px' }}>{uploadSuccess}</div>}
            {syncMessage && (
              <div
                className={syncMessage.type === 'success' ? 'success-message' : 'error-message'}
                style={{ marginTop: '8px' }}
              >
                {syncMessage.text}
              </div>
            )}
          </div>

          {/* Document List */}
          <div className="settings-section">
            <div className="page-controls" style={{ marginBottom: '12px' }}>
              <span className="messages-count">
                {documents.length} document{documents.length !== 1 ? 's' : ''}
              </span>
              <button onClick={fetchDocuments} className="btn-refresh">Refresh</button>
            </div>

            {documents.length === 0 && (
              <div className="no-messages">
                No documents in this event's knowledge base. Upload a document above to get started.
              </div>
            )}

            {documents.length > 0 && (
              <div className="messages-table-wrapper">
                <table className="messages-table">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Size</th>
                      <th>Last Modified</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <React.Fragment key={doc.s3_key}>
                        <tr>
                          <td style={{ wordBreak: 'break-word' }}>{doc.filename}</td>
                          <td>{formatFileSize(doc.size)}</td>
                          <td>{formatDate(doc.last_modified)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handleDownload(doc)}
                                disabled={loadingDownload === doc.s3_key}
                                className="btn-refresh"
                                style={{ fontSize: '0.8rem', padding: '4px 10px' }}
                              >
                                {loadingDownload === doc.s3_key ? 'Loading...' : 'Download'}
                              </button>
                              {deleteConfirm === doc.s3_key ? (
                                <>
                                  <button
                                    onClick={() => handleDelete(doc.s3_key)}
                                    disabled={deleting === doc.s3_key}
                                    style={{
                                      fontSize: '0.8rem', padding: '4px 10px',
                                      background: '#d32f2f', color: '#fff',
                                      border: 'none', borderRadius: '4px', cursor: 'pointer',
                                    }}
                                  >
                                    {deleting === doc.s3_key ? 'Deleting...' : 'Confirm Delete'}
                                  </button>
                                  <button
                                    onClick={handleDeleteCancel}
                                    className="btn-refresh"
                                    style={{ fontSize: '0.8rem', padding: '4px 10px' }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleDeleteConfirm(doc.s3_key)}
                                  style={{
                                    fontSize: '0.8rem', padding: '4px 10px',
                                    background: 'none', color: '#d32f2f',
                                    border: '1px solid #d32f2f', borderRadius: '4px', cursor: 'pointer',
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default KnowledgeManagementPage;
